import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';

import AddressCard from '../../components/addresses/AddressCard';
import AddressForm from '../../components/addresses/AddressForm';
import { Address, AddressCreationAttributes } from '../../models/Address';
import Loading from '../../components/global/loading/Loading';
import { useAddress } from '../../context/AddressContext';

const Addresses: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  // Pobierz context adresów
  const addressContext = useAddress();

  // Rozpakowanie funkcji z kontekstu adresów
  const {
    getUserAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  } = addressContext;

  // Modyfikacja obsługi stanu:

  // Zastąp standardowe setAddresses:
  const safeSetAddresses = (newAddresses: Address[]) => {
    setAddresses(newAddresses);
  };

  // Dodajemy dodatkowy useEffect do śledzenia aktualizacji stanu
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [addresses, isLoading, error, isAddingAddress]);

  // Dodaj na początku funkcji komponenetu:

  // Funkcja do ładowania adresów z lepszą obsługą błędów
  const loadAddresses = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const userAddresses = await getUserAddresses();

      // Dodatkowe sprawdzenie na dane
      if (!userAddresses) {
        console.error('Otrzymano pustą odpowiedź zamiast tablicy adresów');
        safeSetAddresses([]);
      } else {
        safeSetAddresses(Array.isArray(userAddresses) ? userAddresses : []);
      }
    } catch (error: any) {
      console.error('Błąd podczas pobierania adresów:', error);
      setError(
        error?.message ||
          'Nie udało się pobrać adresów. Spróbuj ponownie później.',
      );
      toast.error('Nie udało się pobrać adresów.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  // Obsługa dodawania adresu
  const handleAddAddress = async (data: AddressCreationAttributes) => {
    const toastId = toast.loading('Dodawanie adresu...');
    try {
      const newAddress = await createAddress(data);
      setAddresses([...addresses, newAddress]);
      setIsAddingAddress(false);
      toast.update(toastId, {
        render: 'Adres został dodany pomyślnie!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Nie udało się dodać adresu.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Failed to add address:', error);
    }
  };

  // Obsługa edycji adresu
  const handleEditAddress = async (data: AddressCreationAttributes) => {
    if (!editingAddress) return;

    const toastId = toast.loading('Aktualizacja adresu...');
    try {
      const updatedAddress = await updateAddress(editingAddress.id, data);
      setAddresses(
        addresses.map((addr) =>
          addr.id === updatedAddress.id ? updatedAddress : addr,
        ),
      );
      setEditingAddress(null);
      toast.update(toastId, {
        render: 'Adres został zaktualizowany pomyślnie!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Nie udało się zaktualizować adresu.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Failed to update address:', error);
    }
  };

  // Obsługa usuwania adresu
  const handleDeleteAddress = async (addressId: string) => {
    const toastId = toast.loading('Usuwanie adresu...');
    try {
      await deleteAddress(addressId);
      setAddresses(addresses.filter((addr) => addr.id !== addressId));
      toast.update(toastId, {
        render: 'Adres został usunięty pomyślnie!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Nie udało się usunąć adresu.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Failed to delete address:', error);
    }
  };

  // Obsługa ustawiania adresu domyślnego
  const handleSetDefaultAddress = async (addressId: string) => {
    const toastId = toast.loading('Ustawianie adresu domyślnego...');
    try {
      const updatedAddress = await setDefaultAddress(addressId);

      // Aktualizujemy stan adresów - ustawiamy isDefault=true dla wybranego adresu
      // i isDefault=false dla pozostałych
      setAddresses(
        addresses.map((addr) => ({
          ...addr,
          isDefault: addr.id === updatedAddress.id,
        })),
      );

      toast.update(toastId, {
        render: 'Adres domyślny został zmieniony!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Nie udało się ustawić adresu domyślnego.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Failed to set default address:', error);
    }
  };

  const renderAddressList = () => {
    if (addresses.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-600">
            Nie masz jeszcze żadnych zapisanych adresów.
          </p>
          <button
            onClick={() => setIsAddingAddress(true)}
            className="mt-4 text-primary hover:text-primary-dark font-medium"
          >
            Dodaj swój pierwszy adres
          </button>
        </motion.div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {addresses.map((address) => {
          return (
            <AddressCard
              key={address.id}
              address={address}
              onEdit={() => setEditingAddress(address)}
              onDelete={() => handleDeleteAddress(address.id)}
              onSetDefault={() => handleSetDefaultAddress(address.id)}
            />
          );
        })}
      </div>
    );
  };

  // Widok ładowania
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading />
      </div>
    );
  }

  // Widok błędu
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-800">Moje adresy</h1>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => {
              loadAddresses();
            }}
            className="mt-4 text-primary hover:text-primary-dark font-medium"
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6" data-testid="addresses-component">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Moje adresy</h1>

        {!isAddingAddress && !editingAddress && (
          <button
            onClick={() => {
              setIsAddingAddress(true);
            }}
            className="flex items-center gap-1 text-sm bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
          >
            <AddIcon fontSize="small" /> Dodaj nowy adres
          </button>
        )}
      </div>

      {/* Formularz dodawania lub edycji adresu */}
      <AnimatePresence>
        {isAddingAddress && (
          <AddressForm
            onSubmit={handleAddAddress}
            onCancel={() => setIsAddingAddress(false)}
          />
        )}

        {editingAddress && (
          <AddressForm
            addressToEdit={editingAddress}
            onSubmit={handleEditAddress}
            onCancel={() => setEditingAddress(null)}
          />
        )}
      </AnimatePresence>

      {/* Lista adresów */}
      <div className="space-y-4">{!isAddingAddress && renderAddressList()}</div>
    </div>
  );
};

export default Addresses;
